import type { DocMigration } from "../docMigration";

const builtinTrimProfilesMerged = {
  ["BASE_TRIM_MOLDING_4"]: {
    entityType: "PROFILE",
    id: "BASE_TRIM_MOLDING_4",
    name: '4" Baseboard',
    sortIndex: 0,
    profileType: "CUSTOM",
    polyline: [0, 0, -0.5, 0, -0.5, 4, 0, 4, 0, 0],
  },
  ["BASE_TRIM_MOLDING_6"]: {
    entityType: "PROFILE",
    id: "BASE_TRIM_MOLDING_6",
    name: '6" Baseboard',
    sortIndex: 1,
    profileType: "CUSTOM",
    polyline: [0, 0, -0.5, 0, -0.5, 6, 0, 6, 0, 0],
  },
  ["CROWN_TRIM_MOLDING_1"]: {
    entityType: "PROFILE",
    id: "CROWN_TRIM_MOLDING_1",
    name: "Cove Crown Molding",
    sortIndex: 2,
    profileType: "CUSTOM",
    polyline: [
      0, -5, -1.1, -5, -1.1, -4, -2.1, -4, -2.1, -3, -3.1, -3, -3.1, -2, -4.1, -2, -4.1, -1, -5.1, -1, -5.1, 0, 0, -5,
    ],
  },
  ["CROWN_TRIM_MOLDING_2"]: {
    entityType: "PROFILE",
    id: "CROWN_TRIM_MOLDING_2",
    name: "Simple Crown Molding",
    sortIndex: 3,
    profileType: "CUSTOM",
    polyline: [
      0, 0, 0, -5, -0.2, -5, -0.2, -4, -0.4, -4, -0.4, -3, -0.6, -3, -0.6, -2, -0.8, -2, -0.8, -1, -1, -1, -1, 0, 0, 0,
    ],
  },
  ["DEFAULT_PROFILE"]: {
    entityType: "PROFILE",
    id: "DEFAULT_PROFILE",
    name: "Default Profile",
    sortIndex: 4,
    profileType: "BOARD",
    elevationNoteEnabled: false,
    width: 0.75,
    height: 7.25,
    nominalWidth: 1,
    nominalHeight: 8,
    elevationNote: '8"',
  },
  ["DEFAULT_TRIM_BOARD_1X6"]: {
    entityType: "PROFILE",
    id: "DEFAULT_TRIM_BOARD_1X6",
    name: "Trim Board 1 x 6",
    sortIndex: 5,
    profileType: "BOARD",
    elevationNoteEnabled: false,
    width: 0.75,
    height: 5.5,
    nominalWidth: 1,
    nominalHeight: 6,
    elevationNote: '6"',
  },
  ["DEFAULT_CORNER_BOARD_1X4"]: {
    entityType: "PROFILE",
    id: "DEFAULT_CORNER_BOARD_1X4",
    name: "Corner Board 1 x 4",
    sortIndex: 6,
    profileType: "BOARD",
    elevationNoteEnabled: false,
    width: 1,
    height: 4,
    nominalWidth: 1,
    nominalHeight: 4,
    elevationNote: '4"',
  },
  ["DEFAULT_DORMER_CORNER_BOARD_1X2"]: {
    entityType: "PROFILE",
    id: "DEFAULT_DORMER_CORNER_BOARD_1X2",
    name: "Corner Board 1 x 2",
    sortIndex: 7,
    profileType: "BOARD",
    elevationNoteEnabled: false,
    width: 1,
    height: 2,
    nominalWidth: 1,
    nominalHeight: 2,
    elevationNote: '2"',
  },
};

const builtInTrimProfileIds = [
  "BASE_TRIM_MOLDING_4",
  "BASE_TRIM_MOLDING_6",
  "CROWN_TRIM_MOLDING_1",
  "CROWN_TRIM_MOLDING_2",
  "DEFAULT_PROFILE",
  "DEFAULT_TRIM_BOARD_1X6",
  "DEFAULT_CORNER_BOARD_1X4",
  "DEFAULT_DORMER_CORNER_BOARD_1X2",
] as const;

const builtInTrimProfilesGroupId = "builtInTrimProfilesGroup";

const originalDefaultProfileName = "Exterior Transition Board Trim";

const builtInTrimProfilesGroup = {
  entityType: "GROUP",
  id: builtInTrimProfilesGroupId,
  name: "Built-In Profiles",
  sortIndex: 14,
};

function fillMissingBuiltinTrimProfiles(json: any): void {
  if (json.profiles === undefined || json.profiles === null || typeof json.profiles !== "object") {
    json.profiles = {};
  }

  for (const profileId of builtInTrimProfileIds) {
    if (json.profiles[profileId] === undefined) {
      json.profiles[profileId] = { ...builtinTrimProfilesMerged[profileId] };
    }
  }
}

function renameSeededDefaultProfile(json: any): void {
  const defaultProfile = json.profiles?.DEFAULT_PROFILE;
  if (defaultProfile !== undefined && defaultProfile.name === originalDefaultProfileName) {
    defaultProfile.name = builtinTrimProfilesMerged.DEFAULT_PROFILE.name;
  }
}

function applyBuiltInTrimProfilesEntityGroups(json: any): void {
  if (json.groups === undefined || json.groups === null || typeof json.groups !== "object") {
    json.groups = {};
  }

  const existingGroup = json.groups[builtInTrimProfilesGroupId];
  if (existingGroup === undefined) {
    json.groups[builtInTrimProfilesGroupId] = { ...builtInTrimProfilesGroup };
  } else if (existingGroup.isBuiltIn !== undefined) {
    delete existingGroup.isBuiltIn;
  }

  if (json.entityGroups === undefined || json.entityGroups === null || typeof json.entityGroups !== "object") {
    json.entityGroups = {};
  }

  for (const profileId of builtInTrimProfileIds) {
    const entityGroupKey = `profile.${profileId}`;
    if (json.entityGroups[entityGroupKey] === undefined) {
      json.entityGroups[entityGroupKey] = builtInTrimProfilesGroupId;
    }
  }
}

const migration: DocMigration = {
  id: "20260911_preservebuiltintrimprofiles",
  description:
    "Fills any missing built-in trim profiles without overwriting user edits, renames the seeded DEFAULT_PROFILE display name to Default Profile, and treats Built-In Profiles as an ordinary group (drops isBuiltIn). Does not touch PROFILE_CATALOG_SETTINGS.profileTable rows — copying user catalog profiles into doc.profiles is a separate migration.",
  apply(json: any, _warnings: string[], _errors: string[]): any {
    fillMissingBuiltinTrimProfiles(json);
    renameSeededDefaultProfile(json);
    applyBuiltInTrimProfilesEntityGroups(json);
    return json;
  },
};

export default migration;
